import {
  GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink,
  signInWithEmailAndPassword, type User,
} from "firebase/auth";
import {
  doc, onSnapshot, collection, query, where, orderBy, limit,
} from "firebase/firestore";
import { auth, db, USE_EMULATORS, API_BASE } from "./firebase";
import {
  approveDevice, createTopup, verifyTopup, revokeDevice, sendSignInLink,
  adminSetPricing, adminCredit, adminLookup, ensureWalletWeb,
} from "./api";
import { initAnalytics, track, identify } from "./analytics";

declare const Razorpay: any;
const app = document.getElementById("app")!;
const fmt = (p: number) => "₹" + ((p || 0) / 100).toFixed(2);
const onActivatePage = location.pathname.startsWith("/activate");
// The desktop app opens /activate?code=XXXX — pre-fill and auto-approve it.
const presetCode = (new URLSearchParams(location.search).get("code") || "").trim().toUpperCase();
let autoApproveTried = false;
let unsubs: Array<() => void> = [];

// Which signed-out surface to show: the marketing landing or the sign-in form.
// Activation links always go straight to sign-in.
let publicView: "landing" | "login" = onActivatePage ? "login" : "landing";

// A live snapshot of the signed-in user's state — powers the getting-started
// tracker and the contextual nudges without extra reads.
const ob = {
  balance: 0,
  devices: 0,
  exports: 0,
  downloaded: localStorage.getItem("bs_downloaded") === "1",
};

// ---- inline icon set (stroke icons; no emoji) ------------------------------
const svg = (b: string, w = 24) =>
  `<svg viewBox="0 0 24 24" width="${w}" height="${w}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${b}</svg>`;
const I = {
  logo: `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect x="9" y="5" width="14" height="18" rx="2.5" fill="#3a3550"/>
    <rect x="7" y="7" width="14" height="18" rx="2.5" fill="#F4F1E9"/>
    <rect x="10" y="11.5" width="8" height="1.6" rx=".8" fill="#9a96b5"/>
    <rect x="10" y="15" width="7" height="1.6" rx=".8" fill="#bdb9d0"/>
    <rect x="10" y="18.5" width="5" height="1.6" rx=".8" fill="#bdb9d0"/>
    <circle cx="21" cy="21" r="6.6" fill="#B07A1E"/>
    <circle cx="21" cy="21" r="6.6" stroke="#CE9433"/>
    <path d="M18.2 21l1.9 1.9 3.4-3.7" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  google: `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="#4285F4" d="M21.6 12.2c0-.6-.1-1.3-.2-1.9H12v3.6h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.2z"/><path fill="#34A853" d="M12 22c2.7 0 4.9-.9 6.6-2.4l-3.2-2.5c-.9.6-2 .9-3.4.9-2.6 0-4.8-1.7-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z"/><path fill="#FBBC05" d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.1a10 10 0 0 0 0 9z"/><path fill="#EA4335" d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.6 9.4 5.9 12 5.9z"/></svg>`,
  mail: svg(`<rect x="3" y="5" width="18" height="14" rx="2.4"/><path d="M3.5 7l8.5 5.5L20.5 7"/>`),
  shield: svg(`<path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z"/><path d="M9.2 12l2 2 3.6-4.2"/>`),
  layers: svg(`<path d="M12 3l8.5 4.6L12 12.2 3.5 7.6 12 3z"/><path d="M3.6 12.4 12 17l8.4-4.6"/>`),
  stamp: svg(`<circle cx="12" cy="12" r="8.4"/><path d="M8.4 12l2.4 2.4 4.8-5.2"/>`),
  device: svg(`<rect x="4" y="5" width="16" height="11" rx="2"/><path d="M2.5 20h19"/>`),
  arrow: svg(`<path d="M5 12h13"/><path d="M13 6l6 6-6 6"/>`),
  logout: svg(`<path d="M9 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3"/><path d="M16 16l4-4-4-4"/><path d="M20 12H9"/>`),
  bolt: svg(`<path d="M13 3 5 13h6l-1 8 8-10h-6z"/>`),
  lock: svg(`<rect x="5" y="11" width="14" height="9" rx="2.2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>`),
  info: svg(`<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/>`),
  check: svg(`<path d="M4.5 12.5 9 17 19.5 6.5"/>`),
  spark: svg(`<path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1"/>`),
  card: svg(`<rect x="3" y="6" width="18" height="12" rx="2.4"/><path d="M3 10h18"/><path d="M7 14h3"/>`),
  cog: svg(`<circle cx="12" cy="12" r="3.2"/><path d="M19.4 12a7.4 7.4 0 0 0-.1-1.2l1.8-1.4-1.9-3.2-2.1.9a7 7 0 0 0-2-1.2L14.6 2h-3.7l-.4 2.7a7 7 0 0 0-2 1.2l-2.1-.9-1.9 3.2 1.8 1.4a7 7 0 0 0 0 2.4l-1.8 1.4 1.9 3.2 2.1-.9a7 7 0 0 0 2 1.2l.4 2.7h3.7l.4-2.7a7 7 0 0 0 2-1.2l2.1.9 1.9-3.2-1.8-1.4c.1-.4.1-.8.1-1.2z"/>`),
  // added for the landing page, tour and onboarding
  folder: svg(`<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>`),
  convert: svg(`<path d="M4 8h12l-3-3M20 16H8l3 3"/>`),
  reorder: svg(`<path d="M9 6h11M9 12h11M9 18h11"/><path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01"/>`),
  bookmark: svg(`<path d="M7 4h10a1 1 0 0 1 1 1v15l-6-4-6 4V5a1 1 0 0 1 1-1z"/>`),
  ocr: svg(`<path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"/><path d="M8 12h8"/>`),
  grid: svg(`<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M9 4v16"/>`),
  numbers: svg(`<path d="M9 4 7 20M17 4l-2 16M4 9h16M3 15h16"/>`),
  eye: svg(`<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>`),
  download: svg(`<path d="M12 3v12"/><path d="M7 11l5 5 5-5"/><path d="M5 21h14"/>`),
  play: svg(`<path d="M8 5.5v13l11-6.5z"/>`),
  close: svg(`<path d="M6 6l12 12M18 6 6 18"/>`),
  chevR: svg(`<path d="M9 6l6 6-6 6"/>`),
  chevL: svg(`<path d="M15 6l-6 6 6 6"/>`),
  help: svg(`<circle cx="12" cy="12" r="9"/><path d="M9.3 9.2a2.8 2.8 0 0 1 5.2 1.4c0 1.8-2.5 2-2.5 3.6"/><path d="M12 17h.01"/>`),
  users: svg(`<circle cx="9" cy="8" r="3.2"/><path d="M3.6 20a5.4 5.4 0 0 1 10.8 0"/><path d="M16 5.2a3.2 3.2 0 0 1 0 5.6M17.6 20a5.4 5.4 0 0 0-3-4.9"/>`),
  clock: svg(`<circle cx="12" cy="12" r="9"/><path d="M12 7.5v5l3 2"/>`),
  target: svg(`<circle cx="12" cy="12" r="8.2"/><circle cx="12" cy="12" r="3.4"/><path d="M12 1.8v2.6M12 19.6v2.6M1.8 12h2.6M19.6 12h2.6"/>`),
  apple: `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M16.37 1.43c0 1.06-.4 2.05-1.1 2.82-.76.85-1.98 1.5-3 1.42-.13-1.03.4-2.13 1.06-2.85.74-.82 2.02-1.42 3.04-1.39zM19.9 17.3c-.5 1.15-.74 1.66-1.38 2.68-.9 1.42-2.16 3.19-3.72 3.2-1.4.02-1.75-.9-3.65-.89-1.9.01-2.29.9-3.68.9-1.56.03-2.75-1.53-3.65-2.95C1.6 16.7 1.32 12.2 2.87 9.72c1.1-1.74 2.82-2.76 4.44-2.76 1.65 0 2.69.9 4.05.9 1.32 0 2.13-.9 4.04-.9 1.44 0 2.97.78 4.06 2.14-3.57 1.95-2.99 7.05.44 8.2z"/></svg>`,
  windows: `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M3 5.75 10.4 4.7v7.05H3zM11.32 4.57 21 3.2v8.55h-9.68zM3 12.65h7.4v7.05L3 18.65zM11.32 12.65H21v8.55l-9.68-1.36z"/></svg>`,
};

function toast(msg: string, kind: "ok" | "err" = "ok") {
  const t = document.createElement("div");
  t.className = `toast ${kind}`;
  t.innerHTML = (kind === "ok" ? I.check : I.info) + `<span></span>`;
  t.querySelector("span")!.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => {
    t.style.transition = "opacity .3s ease, transform .3s ease";
    t.style.opacity = "0"; t.style.transform = "translate(-50%, 8px)";
    setTimeout(() => t.remove(), 300);
  }, 3800);
}

/** Reveal-on-scroll for landing sections (with a safety net if IO misfires). */
function observeReveals() {
  const els = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
  const show = (el: HTMLElement) => el.classList.add("in");
  if (!("IntersectionObserver" in window)) { els.forEach(show); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { show(e.target as HTMLElement); io.unobserve(e.target); } });
  }, { rootMargin: "0px 0px -8% 0px", threshold: 0.05 });
  els.forEach((el) => io.observe(el));
  setTimeout(() => els.forEach(show), 1600);
}

/** Smooth-scroll to a card and give it a brief attention pulse. */
function focusCard(sel: string) {
  const el = document.querySelector<HTMLElement>(sel);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.remove("pulse"); void el.offsetWidth; el.classList.add("pulse");
  setTimeout(() => el.classList.remove("pulse"), 2400);
}

// ---- live public pricing (per-page rate + free welcome pages) ---------------
// Signed-out surfaces advertise these numbers. They're fetched once from the
// public /v1/pricing endpoint so the page always shows what the server actually
// charges and grants — change either server-side and the copy follows.
const pub = { price_paise: 25, bonus_pages: 1000 };
const fmtShort = (p: number) => fmt(p).replace(/\.00$/, "");

/** Fill every [data-pub] element from the cache; hide bonus copy if disabled. */
function applyPublicPricing() {
  document.querySelectorAll<HTMLElement>("[data-pub]").forEach((el) => {
    const k = el.dataset.pub;
    if (k === "price") el.textContent = fmtShort(pub.price_paise);
    else if (k === "bonus") el.textContent = pub.bonus_pages.toLocaleString("en-IN");
    else if (k === "bonus-value") el.textContent = fmtShort(pub.bonus_pages * pub.price_paise);
  });
  document.querySelectorAll<HTMLElement>("[data-pub-gate='bonus']").forEach((el) =>
    el.classList.toggle("hidden", pub.bonus_pages <= 0));
}

async function refreshPublicPricing() {
  try {
    const r = await fetch(`${API_BASE}/v1/pricing`);
    if (r.ok) {
      const d = await r.json();
      if (Number.isInteger(d.price_per_page_paise) && d.price_per_page_paise > 0) pub.price_paise = d.price_per_page_paise;
      if (Number.isInteger(d.signup_bonus_pages) && d.signup_bonus_pages >= 0) pub.bonus_pages = d.signup_bonus_pages;
    }
  } catch { /* offline — keep the shipped defaults */ }
  applyPublicPricing();
}

// ---- auth -------------------------------------------------------------------
async function handleEmailLink() {
  if (!isSignInWithEmailLink(auth, location.href)) return;
  let email = localStorage.getItem("emailForSignIn");
  if (!email) email = window.prompt("Confirm your email to finish signing in") || "";
  if (email) {
    await signInWithEmailLink(auth, email, location.href);
    localStorage.removeItem("emailForSignIn");
    history.replaceState({}, "",
      onActivatePage && presetCode ? "/activate?code=" + encodeURIComponent(presetCode) : "/");
  }
}

function signInWithGoogle() {
  track("sign_in_start", { method: "google" });
  signInWithPopup(auth, new GoogleAuthProvider()).catch((e) => toast(e.message, "err"));
}

async function sendLink(email: string) {
  const continueUrl = location.origin +
    (onActivatePage ? "/activate?code=" + encodeURIComponent(presetCode) : "/");
  // Firebase's own passwordless email uses a firebaseapp.com sender + default
  // template — that's what lands in spam. Keep it as the reliable fallback.
  const viaFirebase = () =>
    sendSignInLinkToEmail(auth, email, { url: continueUrl, handleCodeInApp: true });
  // API_BASE is empty when same-origin, so gate only on the flag; sendSignInLink
  // uses a relative path in that case. Backend returns 503 (→ Firebase fallback)
  // until ZEPTOMAIL_TOKEN is set, so this is safe to leave on.
  const useCustom = import.meta.env.VITE_CUSTOM_SIGNIN_EMAIL === "true";
  track("sign_in_start", { method: "email_link" });
  try {
    if (useCustom) await sendSignInLink(email, continueUrl); // branded, from our domain
    else await viaFirebase();
    localStorage.setItem("emailForSignIn", email);
    toast("Sign-in link sent. Check your email.");
  } catch (e: any) {
    // If our branded sender is down, fall back so people can always sign in.
    if (useCustom) {
      try {
        await viaFirebase();
        localStorage.setItem("emailForSignIn", email);
        toast("Sign-in link sent. Check your email.");
        return;
      } catch (e2: any) { toast(e2.message, "err"); return; }
    }
    toast(e.message, "err");
  }
}

// ---- public router (landing vs sign-in) ------------------------------------
function goLogin() { publicView = "login"; renderPublic(); window.scrollTo(0, 0); }
function goLanding() { publicView = "landing"; renderPublic(); window.scrollTo(0, 0); }
function renderPublic() {
  if (onActivatePage || publicView === "login") renderSignedOut();
  else renderLanding();
}

// ---- marketing landing ------------------------------------------------------
const FEATURES = [
  { icon: I.layers,   t: "Merge everything",        d: "Folders, ZIPs, PDFs, images, Word & Excel — every format into one perfect PDF." },
  { icon: I.convert,  t: "Convert to PDF",          d: "Word (.docx) and Excel (.xlsx) files are converted to PDF automatically." },
  { icon: I.reorder,  t: "Organize your way",       d: "Drag to reorder, add or remove — total control over document order." },
  { icon: I.bookmark, t: "Smart bookmarks",         d: "Auto-generate a bookmark outline from your folder & file structure." },
  { icon: I.ocr,      t: "OCR enabled",             d: "Make scanned PDFs searchable so you can find anything, instantly.", seal: true },
  { icon: I.grid,     t: "Truncate large Excel",    d: "Trim huge spreadsheets down to just the rows you need." },
  { icon: I.numbers,  t: "Flexible page numbering", d: "Bates-style numbering with prefix, suffix, position & zero-padding.", seal: true },
  { icon: I.eye,      t: "Preview before export",   d: "See your bundle, page count & structure before you commit to export." },
];

function mockWindow(): string {
  return `
  <div class="mock">
    <div class="mock-bar"><i></i><i></i><i></i><span>${I.stamp} BundleStamp</span></div>
    <div class="mock-body">
      <div class="mock-col">
        <div class="mock-h">Sources</div>
        <div class="mock-drop">${I.layers}<span>Drop files &amp; folders</span></div>
        <div class="mock-file"><span class="fi fi-a">${I.folder}</span><b>Case-2024</b><span class="mx">128p</span></div>
        <div class="mock-file"><span class="fi fi-b">${I.stamp}</span><b>Exhibits.zip</b><span class="mx">64p</span></div>
        <div class="mock-file"><span class="fi fi-c">${I.convert}</span><b>Affidavit.docx</b><span class="mx">12p</span></div>
      </div>
      <div class="mock-col">
        <div class="mock-h">Mode &amp; options</div>
        <div class="mock-pills"><span class="mp on">Merge + Number</span><span class="mp">Merge only</span></div>
        <div class="mock-fields">
          <div class="mf"><span>Prefix</span><b>ABC-</b></div>
          <div class="mf"><span>Start</span><b>1</b></div>
          <div class="mf"><span>Position</span><b>top-right</b></div>
        </div>
        <div class="mock-book">${I.bookmark}<span>Add bookmarks (folder outline)</span><i class="tick">${I.check}</i></div>
        <div class="mock-preview">
          <div><div class="mock-count">204 <span>pages</span></div><span class="mock-cap">Estimated bundle</span></div>
          <button class="mock-export" type="button" tabindex="-1">${I.check} Export</button>
        </div>
      </div>
    </div>
  </div>`;
}

function renderLanding() {
  app.innerHTML = `
  <div class="lp">
    <nav class="lp-nav" id="lpNav">
      <div class="brand"><span class="logo">${I.logo}</span> <span class="wm">BundleStamp</span></div>
      <div class="lp-nav-links">
        <a href="#features">Features</a>
        <a href="#how">How it works</a>
        <a href="#security">Security</a>
        <a href="#pricing">Pricing</a>
        <a href="#faq">FAQ</a>
      </div>
      <div class="lp-nav-cta">
        <button class="btn ghost small" data-go="login">Sign in</button>
        <button class="btn primary small" id="navDownload">${I.download} Download</button>
      </div>
    </nav>

    <header class="lp-hero">
      <div class="lp-hero-copy reveal">
        <span class="lp-badge">${I.lock} 100% private · 100% offline</span>
        <h1>Make perfect PDF bundles<br><span class="seal">in minutes, not hours.</span></h1>
        <p class="lp-lede">Merge folders, ZIPs, PDFs, images, Word &amp; Excel into one clean, bookmarked,
          Bates-numbered PDF — entirely on your laptop. No uploads. Nothing ever leaves your device.</p>
        <div class="lp-cta-row">
          <button class="btn primary lg" id="heroDownload">${I.download} ${downloadCtaLabel()}</button>
          <a class="btn lg" href="#how">${I.play} See how it works</a>
        </div>
        <p class="lp-note">${I.check} Free to install · <span data-pub-gate="bonus"><b data-pub="bonus">1,000</b>&nbsp;free pages when you <button class="linklike accent" data-go="login">create your account</button></span></p>
        <div class="lp-chips">
          <span class="lp-chip gift" data-pub-gate="bonus">${I.spark} <b data-pub="bonus">1,000</b>&nbsp;pages free to start</span>
          <span class="lp-chip">${I.check} No uploads</span>
          <span class="lp-chip">${I.check} No data leaves your device</span>
          <span class="lp-chip">${I.check} Windows &amp; macOS</span>
        </div>
      </div>
      <div class="lp-hero-art reveal">
        ${mockWindow()}
        <div class="hero-seal">${I.lock}<b>100%</b><span>OFFLINE</span></div>
      </div>
    </header>

    <div class="lp-trust reveal">
      <span class="lp-trust-label">Built for professionals</span>
      <span class="lp-trust-list">${I.users} Lawyers · Chartered Accountants · Consultants · Legal teams · Businesses</span>
    </div>

    <div class="lp-valueprops reveal">
      <div class="vp"><span class="vp-ic">${I.clock}</span><div><b>Save hours</b><span>What took hours now takes minutes.</span></div></div>
      <div class="vp"><span class="vp-ic">${I.target}</span><div><b>Accurate &amp; reliable</b><span>Built for pros who demand perfection.</span></div></div>
      <div class="vp"><span class="vp-ic">${I.shield}</span><div><b>Private &amp; secure</b><span>No uploads — 100% on your laptop.</span></div></div>
      <div class="vp"><span class="vp-ic">${I.users}</span><div><b>Built for pros</b><span>Lawyers, CAs, consultants &amp; teams.</span></div></div>
    </div>

    <section class="lp-section" id="how">
      <div class="lp-head reveal">
        <span class="eyebrow">How it works</span>
        <h2>Three steps to a perfect bundle</h2>
        <p>No learning curve. Install once, then let BundleStamp handle the tedious parts.</p>
      </div>
      <div class="lp-steps">
        <div class="lp-step reveal"><div class="n">1</div><h3>Download &amp; sign in</h3><p>Install BundleStamp for Windows or macOS and sign in once.<span data-pub-gate="bonus"> Your account starts with <b data-pub="bonus">1,000</b> free pages.</span></p></div>
        <div class="lp-step reveal"><div class="n">2</div><h3>Drop in your files</h3><p>Add folders, ZIPs and files. Reorder, number and bookmark them exactly how you want.</p></div>
        <div class="lp-step reveal"><div class="n">3</div><h3>Preview &amp; export</h3><p>Check your bundle and page count, then export. You're billed only for the pages you export.</p></div>
      </div>
    </section>

    <section class="lp-section" id="features">
      <div class="lp-head reveal">
        <span class="eyebrow">Everything in one tool</span>
        <h2>One app for the whole bundle</h2>
        <p>Merge, convert, organize, number and search — all the steps that used to take hours, now in a few clicks.</p>
      </div>
      <div class="lp-features">
        ${FEATURES.map((f) => `
          <div class="feature reveal ${f.seal ? "seal" : ""}">
            <div class="fi">${f.icon}</div>
            <h3>${f.t}</h3>
            <p>${f.d}</p>
          </div>`).join("")}
      </div>
    </section>

    <section class="lp-section" id="audience">
      <div class="lp-head reveal">
        <span class="eyebrow">Built for professionals like you</span>
        <h2>Made for the work you actually do</h2>
        <p>If your day means assembling documents that have to be right, BundleStamp was built for you.</p>
      </div>
      <div class="lp-audience">
        <div class="aud reveal"><span class="aud-ic">${I.shield}</span><b>Lawyers &amp; legal teams</b><p>Bates-number exhibits and merge case files into one paginated, bookmarked PDF.</p></div>
        <div class="aud reveal"><span class="aud-ic">${I.card}</span><b>Chartered Accountants</b><p>Bundle statements, ledgers and annexures with clean, continuous numbering.</p></div>
        <div class="aud reveal"><span class="aud-ic">${I.spark}</span><b>Consultants</b><p>Assemble polished, bookmarked report packs from mixed Word, Excel &amp; PDF files.</p></div>
        <div class="aud reveal"><span class="aud-ic">${I.layers}</span><b>Businesses</b><p>Combine contracts, invoices and records into organized, searchable documents.</p></div>
      </div>
    </section>

    <div class="lp-secure-wrap" id="security">
      <section class="lp-secure">
        <div class="reveal">
          <span class="eyebrow on-ink">Private by design</span>
          <h2>Your documents never leave your <span class="seal">laptop.</span></h2>
          <p>BundleStamp runs entirely on your device. No uploads, no cloud processing, no copies on
            someone else's server — the way sensitive legal and financial documents should be handled.</p>
          <ul class="lp-secure-points">
            <li><span class="pi">${I.lock}</span><div><b>100% offline processing</b><span>Every merge, convert and export happens locally.</span></div></li>
            <li><span class="pi">${I.shield}</span><div><b>No uploads, ever</b><span>Your files are never sent anywhere — not even to us.</span></div></li>
            <li><span class="pi">${I.card}</span><div><b>Prepaid &amp; transparent</b><span>A simple wallet. You're only charged for pages you export.</span></div></li>
          </ul>
        </div>
        <div class="lp-secure-panel reveal">
          <div class="sp-row"><span class="sp-k">Files uploaded to the cloud</span><span class="sp-v">0</span></div>
          <div class="sp-row"><span class="sp-k">Data leaving your device</span><span class="sp-v">None</span></div>
          <div class="sp-row"><span class="sp-k">Processing location</span><span class="sp-v">Your laptop</span></div>
          <div class="sp-seal">${I.shield}<div><b>Local-only guarantee</b><span>Nothing to leak, nothing to breach.</span></div></div>
        </div>
      </section>
    </div>

    <section class="lp-section" id="pricing">
      <div class="lp-head reveal">
        <span class="eyebrow">Simple pricing</span>
        <h2>Start free, then pay only for what you export</h2>
        <p>No subscription, no per-seat fees. Top up a prepaid wallet and spend it as you go.</p>
      </div>
      <div class="lp-pricing-card reveal">
        <div class="pc-badge">${I.stamp} Pay per page</div>
        <div class="pc-head">Prepaid wallet</div>
        <div class="pc-rate"><b data-pub="price">₹0.25</b><span>per exported page</span></div>
        <p class="pc-sub">Add any amount, anytime. You're charged only for the pages you
          export — and nothing for the pages you don't.</p>
        <ul class="pc-list">
          <li data-pub-gate="bonus">${I.spark} <span>Start with <b data-pub="bonus">1,000</b> free pages (worth <span data-pub="bonus-value">₹250</span>) — on us</span></li>
          <li>${I.check} Only exported pages are billed</li>
          <li>${I.check} Top up ₹500, ₹2,000, ₹5,000 or a custom amount</li>
          <li>${I.check} Merge, preview &amp; organize for free</li>
          <li>${I.check} Unlimited devices on one account</li>
        </ul>
        <button class="btn primary lg block" data-go="login">Create your free account ${I.arrow}</button>
      </div>
    </section>

    <section class="lp-section" id="faq">
      <div class="lp-head reveal">
        <span class="eyebrow">FAQ</span>
        <h2>Questions, answered</h2>
      </div>
      <div class="lp-faq reveal">
        <details open data-pub-gate="bonus"><summary>Is it free to try?<span class="chev">${I.chevR}</span></summary>
          <p>Yes. Installing is free, and every new account starts with <b data-pub="bonus">1,000</b> free pages
          (worth <span data-pub="bonus-value">₹250</span>) — enough to export real bundles and see if it fits your work.
          After that you pay <span data-pub="price">₹0.25</span> per exported page from a prepaid wallet.</p></details>
        <details><summary>Do my files get uploaded?<span class="chev">${I.chevR}</span></summary>
          <p>No. All merging, conversion, numbering and OCR happen on your computer. The app only talks to our
          servers to manage your account and count the pages you export — never to receive your documents.</p></details>
        <details><summary>How does billing work?<span class="chev">${I.chevR}</span></summary>
          <p>You top up a prepaid wallet and pay <span data-pub="price">₹0.25</span> per exported page. The exact
          page count and price are shown before you export, and cancelled or failed exports are never charged.</p></details>
        <details><summary>Can I use it on more than one computer?<span class="chev">${I.chevR}</span></summary>
          <p>Yes. Activate any number of devices with a short code — they all share one wallet, and you can
          revoke a device anytime from your dashboard.</p></details>
        <details><summary>What file types can I bundle?<span class="chev">${I.chevR}</span></summary>
          <p>PDFs, Word (.docx), Excel (.xlsx), images and whole folders or ZIPs — even nested ones. Everything
          is converted and merged into a single clean, bookmarked, page-numbered PDF.</p></details>
      </div>
    </section>

    <section class="lp-final" id="download">
      <div class="lp-download-card reveal">
        <div class="dlc-copy">
          <span class="eyebrow on-ink">Get the app</span>
          <h2>Download BundleStamp for your computer</h2>
          <p>Free to install<span data-pub-gate="bonus"> · <b data-pub="bonus">1,000</b> free pages to start</span> · works 100% offline. Your files never leave your device.</p>
        </div>
        <div class="dlc-actions">
          <button class="btn white lg dlc-btn" data-dl="mac">${I.apple} Download for macOS</button>
          <button class="btn white lg dlc-btn" data-dl="win">${I.windows} Download for Windows</button>
          <span class="dlc-note">${I.lock} No card to install · <button class="linklike on-ink" data-go="login">or create your account first →</button></span>
        </div>
      </div>
    </section>

    <footer class="lp-footer">
      <div class="lp-footer-in">
        <div class="brand"><span class="logo">${I.logo}</span> <span class="wm">BundleStamp</span></div>
        <div class="lp-footer-links">
          <a href="#features">Features</a><a href="#security">Security</a><a href="#pricing">Pricing</a>
          <button class="linklike" data-go="login">Sign in</button>
        </div>
        <span class="lp-footer-copy">© BundleStamp · Smart PDF bundling, done privately.</span>
      </div>
    </footer>
  </div>`;

  track("screen_view", { screen_name: "landing" });
  app.querySelectorAll<HTMLElement>("[data-go='login']").forEach((b) => (b.onclick = () => openSignin("landing")));
  document.getElementById("navDownload")?.addEventListener("click", () => openDownload("nav"));
  document.getElementById("heroDownload")?.addEventListener("click", () => openDownload("hero"));
  app.querySelectorAll<HTMLButtonElement>(".lp-download-card [data-dl]").forEach((el) =>
    (el.onclick = () => startDownload(el.dataset.dl as "mac" | "win")));
  // in-page smooth scrolling
  app.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => (a.onclick = (e) => {
    const el = document.getElementById(a.getAttribute("href")!.slice(1));
    if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
  }));
  const nav = document.getElementById("lpNav")!;
  nav.querySelector(".brand")!.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
  const onScroll = () => nav.classList.toggle("scrolled", window.scrollY > 8);
  onScroll(); window.addEventListener("scroll", onScroll, { passive: true });
  observeReveals();
  applyPublicPricing();
}

// ---- sign-in ----------------------------------------------------------------
function renderSignedOut() {
  app.innerHTML = `
    <div class="auth-shell">
      <aside class="auth-aside">
        <div class="brand"><span class="logo">${I.logo}</span> <span class="wm">BundleStamp</span></div>
        <div class="aside-body">
          <div class="eyebrow on-ink">Document bundling, done right</div>
          <h1>Merge, number &amp; <span class="seal">seal</span> every bundle.</h1>
          <p class="lede">Turn folders, loose files and ZIPs into clean, bookmarked,
            Bates-numbered PDFs — billed only for what you export.</p>
          <ul class="props">
            <li><span class="pi">${I.layers}</span><div><b>Merge anything</b><span>Folders, nested ZIPs and loose files, in natural order.</span></div></li>
            <li><span class="pi">${I.stamp}</span><div><b>Bates-style numbering</b><span>Continuous page numbers, prefixes and bookmarks.</span></div></li>
            <li><span class="pi">${I.lock}</span><div><b>Private by design</b><span>Everything runs locally — your files never leave your device.</span></div></li>
          </ul>
        </div>
        <div class="trust">
          <span>${I.shield} Local processing</span>
          <span data-pub-gate="bonus">${I.spark} <b data-pub="bonus">1,000</b>&nbsp;pages free</span>
          <span>${I.bolt} Pay per page</span>
        </div>
      </aside>

      <main class="auth-main">
        ${onActivatePage ? "" : `<button class="lp-back" id="backHome">${I.chevL} Back to home</button>`}
        <div class="auth-card">
          <div class="brand mobile-brand"><span class="logo">${I.logo}</span> <span class="wm">BundleStamp</span></div>
          <h2>${onActivatePage ? "Activate your device" : "Sign in or sign up"}</h2>
          <p class="sub">${onActivatePage
            ? "Sign in to connect this device to your account."
            : "Use Google or an email link — we'll create your account automatically if you're new."}</p>
          ${onActivatePage ? `<div class="banner">${I.device}<span>You're signing in to <b>activate a device</b>. We'll connect it automatically.</span></div>`
            : `<div class="bonus-chip" data-pub-gate="bonus">${I.spark}<span>New accounts start with <b data-pub="bonus">1,000</b> free pages — worth <span data-pub="bonus-value">₹250</span>, on us.</span></div>`}
          <button id="google" class="btn google block">${I.google} Continue with Google</button>
          <div class="divider">or</div>
          <div class="field" style="gap:10px">
            <input id="email" type="email" placeholder="you@company.com" autocomplete="email" />
            <button id="sendlink" class="btn primary block">${I.mail} Email me a sign-in link</button>
          </div>
          ${USE_EMULATORS ? `
            <div class="divider">dev (emulator)</div>
            <div class="dev-block">
              <input id="devEmail" type="email" placeholder="demo@bundlestamp.test" value="demo@bundlestamp.test" />
              <input id="devPass" type="password" placeholder="password" value="password123" />
              <button id="devSignin" class="btn block">Dev sign-in</button>
            </div>` : ""}
          <p class="legal">Need the app? <button class="linklike accent" id="loginDownload">Download for Windows / macOS</button><br>
            By continuing you agree to our Terms &amp; Privacy.</p>
          ${onActivatePage ? "" : `
          <div class="next-steps">
            <span class="ns-title">What happens next</span>
            <ol>
              <li><span>1</span> Download the desktop app</li>
              <li><span>2</span> Activate this device with its code</li>
              <li><span>3</span> Export your first bundle</li>
            </ol>
          </div>`}
        </div>
      </main>
    </div>`;
  (document.getElementById("google") as HTMLButtonElement).onclick = signInWithGoogle;
  (document.getElementById("sendlink") as HTMLButtonElement).onclick = () => {
    const email = (document.getElementById("email") as HTMLInputElement).value.trim();
    if (email) sendLink(email);
  };
  track("screen_view", { screen_name: onActivatePage ? "activate" : "login" });
  applyPublicPricing();
  const back = document.getElementById("backHome");
  if (back) back.onclick = goLanding;
  document.getElementById("loginDownload")?.addEventListener("click", () => openDownload("login"));
  if (USE_EMULATORS) {
    (document.getElementById("devSignin") as HTMLButtonElement).onclick = () => {
      const email = (document.getElementById("devEmail") as HTMLInputElement).value.trim();
      const pass = (document.getElementById("devPass") as HTMLInputElement).value;
      signInWithEmailAndPassword(auth, email, pass).catch((e) => toast(e.message, "err"));
    };
  }
}

function renderDashboard(user: User, isAdmin: boolean) {
  const email = user.email || "Signed in";
  const initials = (email.match(/[a-zA-Z]/)?.[0] || "U").toUpperCase() +
    (email.split("@")[0]?.replace(/[^a-zA-Z]/g, "")?.[1]?.toUpperCase() || "");
  const greetName = email.split("@")[0] || "there";
  app.innerHTML = `
    <header class="appbar">
      <div class="brand"><span class="logo">${I.logo}</span> <span class="wm">BundleStamp</span></div>
      <div class="who">
        <button id="dlApp" class="btn small primary">${I.download} Download app</button>
        <button id="guide" class="btn small ghost">${I.help} Guide</button>
        <span class="avatar">${initials}</span>
        <span class="who-email">${email}</span>
        <button id="signout" class="btn small ghost">${I.logout} Sign out</button>
      </div>
    </header>
    <main class="dash">
      <div class="dash-head">
        <h1>Welcome back, ${greetName}.</h1>
        <p>Manage your wallet, devices and exports — all in one place.</p>
      </div>
      <div class="dash-grid">
        <section class="card wallet on-ink-ui">
          <div class="wlabel"><span>Wallet balance</span>
            <span class="seal-chip">${I.stamp} BundleStamp credit</span></div>
          <div id="balance" class="balance">₹—</div>
          <div id="pages" class="pages">— pages remaining</div>
          <div class="spark" id="spark">${'<i style="height:30%"></i><i style="height:55%"></i><i style="height:40%"></i><i style="height:70%"></i><i style="height:50%"></i><i style="height:85%"></i><i style="height:60%"></i><i style="height:95%"></i><i style="height:72%"></i><i style="height:100%"></i>'}</div>
          <div class="price">${I.info}<span id="price"></span></div>
          <div id="walletNudge" class="wallet-nudge hidden"></div>
          <div class="topups">
            <button class="chip add" data-topup="50000">+ ₹500</button>
            <button class="chip" data-topup="200000">+ ₹2,000</button>
            <button class="chip" data-topup="500000">+ ₹5,000</button>
          </div>
          <div class="custom-row">
            <input id="customAmt" type="number" placeholder="Custom ₹ amount" min="100" />
            <button id="customTopup" class="btn">Top up</button>
          </div>
        </section>

        <section class="card activate">
          <h2>${I.device} Activate a device</h2>
          <p class="steps-mini">Open BundleStamp on your PC and choose <b>Sign in</b>.
            Enter the <b>code</b> it shows below to link it to your account.</p>
          <div class="inline">
            <input id="userCode" type="text" placeholder="WXYZ-1234" autocomplete="off" />
            <button id="approve" class="btn primary">${I.check} Activate</button>
          </div>
          <h3>Your devices</h3>
          <ul id="devices" class="list"></ul>
        </section>

        <section class="card span2" id="usageCard">
          <h2>${I.spark} Usage &amp; spend</h2>
          <div class="stats">
            <div class="stat-tile"><div class="k">Total spent</div><div class="v" id="statSpent">₹0.00</div></div>
            <div class="stat-tile"><div class="k">Pages exported</div><div class="v" id="statPages">0</div></div>
            <div class="stat-tile"><div class="k">Exports</div><div class="v" id="statExports">0</div></div>
          </div>
          <div class="usage-wrap">
            <table class="usage">
              <thead><tr><th>Date</th><th>Mode</th><th>Pages</th><th>Cost</th><th>Status</th></tr></thead>
              <tbody id="jobs"><tr><td colspan="5" class="empty-soft">No exports yet — your bundles will appear here.</td></tr></tbody>
            </table>
          </div>
        </section>

        <section class="card cap span2">
          <h2>${I.spark} Make the most of BundleStamp</h2>
          <p class="hint">Open the desktop app to put these to work — everything runs locally on your machine.</p>
          <div class="cap-grid">
            <span class="cap-chip">${I.layers} Merge folders, ZIPs &amp; files</span>
            <span class="cap-chip">${I.convert} Convert Word &amp; Excel</span>
            <span class="cap-chip">${I.bookmark} Smart bookmarks</span>
            <span class="cap-chip">${I.ocr} OCR searchable scans</span>
            <span class="cap-chip">${I.numbers} Bates numbering</span>
            <span class="cap-chip">${I.eye} Preview before export</span>
            <button class="cap-chip cap-tour" id="capTour">${I.play} Take the 60-second tour</button>
          </div>
        </section>
        ${isAdmin ? adminCardHtml() : ""}
      </div>
    </main>`;

  (document.getElementById("signout") as HTMLButtonElement).onclick = () => { publicView = "landing"; signOut(auth); };
  track("screen_view", { screen_name: "dashboard" });
  (document.getElementById("dlApp") as HTMLButtonElement).onclick = () => openDownload("dashboard_header");
  (document.getElementById("guide") as HTMLButtonElement).onclick = () => openTour(0);
  (document.getElementById("capTour") as HTMLButtonElement).onclick = () => openTour(0);
  app.querySelectorAll<HTMLButtonElement>("[data-topup]").forEach((b) =>
    (b.onclick = () => topup(parseInt(b.dataset.topup!, 10))));
  (document.getElementById("customTopup") as HTMLButtonElement).onclick = () => {
    const rupees = parseInt((document.getElementById("customAmt") as HTMLInputElement).value || "0", 10);
    if (rupees >= 100) topup(rupees * 100); else toast("Minimum ₹100", "err");
  };
  (document.getElementById("approve") as HTMLButtonElement).onclick = doApprove;
  if (onActivatePage) {
    const codeInput = document.getElementById("userCode") as HTMLInputElement;
    if (presetCode) {
      codeInput.value = presetCode;
      // Auto-approve once — the user just signed in via the app's link.
      if (!autoApproveTried) { autoApproveTried = true; void doApprove(); }
    }
    codeInput.focus();
  }
  if (isAdmin) setupAdmin();

  subscribe(user);
}

// ---- download ---------------------------------------------------------------
function detectOS(): "mac" | "win" | "other" {
  const s = (navigator.userAgent + " " + (navigator.platform || "")).toLowerCase();
  if (s.includes("mac") || s.includes("iphone") || s.includes("ipad")) return "mac";
  if (s.includes("win")) return "win";
  return "other";
}

function downloadCtaLabel(): string {
  const os = detectOS();
  if (os === "mac") return "Download for Mac";
  if (os === "win") return "Download for Windows";
  return "Download free";
}

let lastDownloadSource = "unknown";

/** Hitting download opens the /download page in a new tab (it starts the file). */
function openDownload(source = "unknown") {
  lastDownloadSource = source;
  track("download_open", { source });
  ob.downloaded = true;
  localStorage.setItem("bs_downloaded", "1");
  const os = detectOS();
  const p = os === "mac" ? "macos" : os === "win" ? "windows" : "";
  window.open("/download" + (p ? "?os=" + p : ""), "_blank", "noopener");
}

function startDownload(platform: "mac" | "win") {
  track("download_click", { platform: platform === "mac" ? "macos" : "windows", source: lastDownloadSource });
  ob.downloaded = true;
  localStorage.setItem("bs_downloaded", "1");
  window.open("/download?os=" + (platform === "mac" ? "macos" : "windows"), "_blank", "noopener");
}

/** Remove any open modal overlays (they live on <body>, outside #app). */
function removeOverlays() {
  ["signinMask", "dlMask"].forEach((id) => document.getElementById(id)?.remove());
}

// ---- sign-in (modal — no full-page detour) ---------------------------------
function openSignin(source = "unknown") {
  track("signin_open", { source });
  let mask = document.getElementById("signinMask");
  if (!mask) {
    mask = document.createElement("div");
    mask.id = "signinMask";
    mask.className = "tour-mask";
    document.body.appendChild(mask);
  }
  renderSignin();
}

function closeSignin() {
  const m = document.getElementById("signinMask");
  if (m) { m.style.opacity = "0"; setTimeout(() => m.remove(), 200); }
}

function renderSignin() {
  const mask = document.getElementById("signinMask");
  if (!mask) return;
  mask.innerHTML = `
    <div class="dl signin" role="dialog" aria-modal="true" aria-label="Sign in to BundleStamp">
      <button class="tour-x" id="siX" aria-label="Close">${I.close}</button>
      <div class="dl-head">
        <span class="dl-logo">${I.logo}</span>
        <h2>Sign in or sign up</h2>
        <p>Use Google or an email link — we'll create your account automatically if you're new.</p>
      </div>
      <div class="signin-body">
        <div class="bonus-chip" data-pub-gate="bonus">${I.spark}<span>New accounts start with <b data-pub="bonus">1,000</b> free pages — on us.</span></div>
        <button id="siGoogle" class="btn google block">${I.google} Continue with Google</button>
        <div class="divider">or</div>
        <div class="field" style="gap:10px">
          <input id="siEmail" type="email" placeholder="you@company.com" autocomplete="email" />
          <button id="siSend" class="btn primary block">${I.mail} Email me a sign-in link</button>
        </div>
        ${USE_EMULATORS ? `
          <div class="divider">dev</div>
          <div class="dev-block">
            <input id="siDevEmail" type="email" value="demo@bundlestamp.test" />
            <input id="siDevPass" type="password" value="password123" />
            <button id="siDev" class="btn block">Dev sign-in</button>
          </div>` : ""}
        <p class="legal">By continuing you agree to our Terms &amp; Privacy.</p>
      </div>
    </div>`;
  (document.getElementById("siX") as HTMLButtonElement).onclick = closeSignin;
  (document.getElementById("siGoogle") as HTMLButtonElement).onclick = signInWithGoogle;
  (document.getElementById("siSend") as HTMLButtonElement).onclick = () => {
    const email = (document.getElementById("siEmail") as HTMLInputElement).value.trim();
    if (email) sendLink(email);
  };
  if (USE_EMULATORS) {
    (document.getElementById("siDev") as HTMLButtonElement).onclick = () => {
      const email = (document.getElementById("siDevEmail") as HTMLInputElement).value.trim();
      const pass = (document.getElementById("siDevPass") as HTMLInputElement).value;
      signInWithEmailAndPassword(auth, email, pass).catch((e) => toast(e.message, "err"));
    };
  }
  mask.onclick = (e) => { if (e.target === mask) closeSignin(); };
  applyPublicPricing();
  (document.getElementById("siEmail") as HTMLInputElement | null)?.focus();
}

function updateWalletNudge(balance: number, pages: number, bonus?: { paise: number; pages: number }) {
  const el = document.getElementById("walletNudge");
  if (!el) return;
  if (balance <= 0) {
    el.className = "wallet-nudge zero";
    el.innerHTML = `${I.bolt}<span>Add credit to start exporting bundles.</span>`;
  } else if (pages > 0 && pages <= 20) {
    el.className = "wallet-nudge warn";
    el.innerHTML = `${I.info}<span>Running low — about ${pages} page${pages === 1 ? "" : "s"} left. Top up to avoid interruptions.</span>`;
  } else if (bonus && bonus.paise > 0 && balance === bonus.paise) {
    // Welcome credit still untouched — tell the new user it's there and real.
    el.className = "wallet-nudge welcome";
    el.innerHTML = `${I.spark}<span>Welcome gift loaded: <b>${bonus.pages.toLocaleString("en-IN")} free pages</b>. Download the app and export your first bundle on us.</span>`;
  } else {
    el.className = "wallet-nudge hidden";
    el.innerHTML = "";
  }
}

// ---- welcome tour (poster-inspired walkthrough) ----------------------------
const TOUR = [
  {
    art: `<div class="tour-art art-1"><span class="tour-logo">${I.logo}</span><span class="lp-badge">${I.lock} 100% private · 100% offline</span></div>`,
    eyebrow: "Welcome",
    h: "Welcome to BundleStamp",
    p: `Turn stacks of folders, ZIPs and files into one clean, numbered, bookmarked PDF — in minutes, and entirely on your own computer.<span data-pub-gate="bonus"> Your first <b data-pub="bonus">1,000</b> pages are on us.</span>`,
  },
  {
    art: `<div class="tour-art art-2">
      <div class="ta-step"><span>1</span>Download &amp; sign in</div>
      <div class="ta-arrow">${I.chevR}</div>
      <div class="ta-step"><span>2</span>Drop in files</div>
      <div class="ta-arrow">${I.chevR}</div>
      <div class="ta-step"><span>3</span>Preview &amp; export</div>
    </div>`,
    eyebrow: "How it works",
    h: "Three steps to a perfect bundle",
    p: "Add folders, ZIPs and files, reorder and number them, then preview and export. You only pay for the pages you export.",
  },
  {
    art: `<div class="tour-art art-3">${
      ["Merge everything", "Convert Word &amp; Excel", "Smart bookmarks", "OCR search", "Bates numbering", "Preview first"]
        .map((t) => `<span class="ta-chip">${I.check} ${t}</span>`).join("")
    }</div>`,
    eyebrow: "What you can do",
    h: "Everything in one tool",
    p: "Merge, convert, organize, bookmark, OCR and number — all offline. Ready to set up your device?",
  },
];
let tourStep = 0;

function openTour(i = 0) {
  tourStep = i;
  track("tour_open", { step: i });
  let mask = document.getElementById("tourMask");
  if (!mask) {
    mask = document.createElement("div");
    mask.id = "tourMask";
    mask.className = "tour-mask";
    document.body.appendChild(mask);
  }
  renderTourStep();
}

function closeTour() {
  localStorage.setItem("bs_tour_seen", "1");
  const mask = document.getElementById("tourMask");
  if (mask) { mask.style.opacity = "0"; setTimeout(() => mask.remove(), 200); }
}

function renderTourStep() {
  const mask = document.getElementById("tourMask");
  if (!mask) return;
  const s = TOUR[tourStep];
  const last = tourStep === TOUR.length - 1;
  mask.innerHTML = `
    <div class="tour" role="dialog" aria-modal="true" aria-label="Welcome to BundleStamp">
      <button class="tour-x" id="tourX" aria-label="Close">${I.close}</button>
      ${s.art}
      <div class="tour-body">
        <span class="eyebrow">${s.eyebrow}</span>
        <h2>${s.h}</h2>
        <p>${s.p}</p>
      </div>
      <div class="tour-foot">
        <div class="tour-dots">${TOUR.map((_, i) => `<i class="${i === tourStep ? "on" : ""}"></i>`).join("")}</div>
        <div class="tour-nav">
          ${tourStep > 0
            ? `<button class="btn ghost small" id="tourBack">${I.chevL} Back</button>`
            : `<button class="btn ghost small" id="tourSkip">Skip</button>`}
          <button class="btn primary small" id="tourNext">${last ? "Get started " + I.arrow : "Next " + I.arrow}</button>
        </div>
      </div>
    </div>`;
  applyPublicPricing();
  (document.getElementById("tourX") as HTMLButtonElement).onclick = closeTour;
  document.getElementById("tourSkip")?.addEventListener("click", closeTour);
  document.getElementById("tourBack")?.addEventListener("click", () => { tourStep--; renderTourStep(); });
  (document.getElementById("tourNext") as HTMLButtonElement).onclick = () => {
    if (last) { track("tour_complete"); closeTour(); }
    else { tourStep++; renderTourStep(); }
  };
  mask.onclick = (e) => { if (e.target === mask) closeTour(); };
}

function maybeAutoTour() {
  if (localStorage.getItem("bs_tour_seen") === "1") return;
  setTimeout(() => { if (auth.currentUser) openTour(0); }, 500);
}

// ---- admin panel (only rendered for users with the admin claim) ------------
function adminCardHtml(): string {
  return `
    <section class="card span2 admin">
      <h2>${I.cog} Admin</h2>
      <div class="admin-grid">
        <div>
          <h3>Global pricing</h3>
          <div class="field">
            <span>Price per exported page</span>
            <div class="inline">
              <input id="adminPrice" type="number" step="0.01" min="0.01" placeholder="0.50" class="rupee" />
              <button id="adminPriceSave" class="btn primary">Save</button>
            </div>
          </div>
        </div>
        <div>
          <h3>Look up user</h3>
          <div class="inline">
            <input id="adminEmail" type="email" placeholder="user@company.com" />
            <button id="adminLookupBtn" class="btn">Find</button>
          </div>
          <div id="adminUser" class="muted small"></div>
        </div>
        <div>
          <h3>Manual credit / debit</h3>
          <div class="field">
            <input id="adminUid" type="text" placeholder="user uid" />
            <div class="inline">
              <input id="adminAmount" type="number" step="0.01" placeholder="±amount" />
              <input id="adminNote" type="text" placeholder="reason" />
              <button id="adminCreditBtn" class="btn primary">Apply</button>
            </div>
          </div>
        </div>
      </div>
    </section>`;
}

function setupAdmin() {
  (document.getElementById("adminPriceSave") as HTMLButtonElement).onclick = async () => {
    const rupees = parseFloat((document.getElementById("adminPrice") as HTMLInputElement).value || "0");
    const paise = Math.round(rupees * 100);
    if (paise <= 0) return toast("Enter a price > 0", "err");
    try { await adminSetPricing(paise); toast(`Price set to ${fmt(paise)}/page.`); }
    catch (e: any) { toast(e.message, "err"); }
  };

  (document.getElementById("adminLookupBtn") as HTMLButtonElement).onclick = async () => {
    const email = (document.getElementById("adminEmail") as HTMLInputElement).value.trim();
    if (!email) return;
    try {
      const u = await adminLookup(email);
      (document.getElementById("adminUid") as HTMLInputElement).value = u.uid;
      const jobs = u.recent_jobs.map((j) => `${j.mode} · ${j.pages}p · ${fmt(j.cost_paise)} · ${j.status}`).join("<br>") || "no exports";
      document.getElementById("adminUser")!.innerHTML =
        `<b>${u.email}</b> — balance ${fmt(u.wallet.balance_paise)} (${u.wallet.pages_remaining} pages)<br>${jobs}`;
    } catch (e: any) { toast(e.message, "err"); }
  };

  (document.getElementById("adminCreditBtn") as HTMLButtonElement).onclick = async () => {
    const uid = (document.getElementById("adminUid") as HTMLInputElement).value.trim();
    const rupees = parseFloat((document.getElementById("adminAmount") as HTMLInputElement).value || "0");
    const note = (document.getElementById("adminNote") as HTMLInputElement).value.trim();
    const paise = Math.round(rupees * 100);
    if (!uid || paise === 0) return toast("Enter a uid and a non-zero amount", "err");
    try {
      const r = await adminCredit(uid, paise, note);
      toast(`Adjusted. New balance ${fmt(r.balance_paise)}.`);
    } catch (e: any) { toast(e.message, "err"); }
  };
}

// ---- live data --------------------------------------------------------------
let priceCache = 25;
function subscribe(user: User) {
  unsubs.forEach((u) => u());
  unsubs = [];
  const balEl = document.getElementById("balance")!;
  const pagesEl = document.getElementById("pages")!;
  const priceEl = document.getElementById("price")!;

  let bonusInfo: { paise: number; pages: number } | undefined;
  const render = (balance: number) => {
    balEl.textContent = fmt(balance);
    const pages = priceCache > 0 ? Math.floor(balance / priceCache) : 0;
    pagesEl.textContent = `${pages.toLocaleString()} pages remaining`;
    priceEl.textContent = `Billed at ${fmt(priceCache)} per exported page.`;
    ob.balance = balance;
    updateWalletNudge(balance, pages, bonusInfo);
  };
  let lastBalance = 0;
  render(lastBalance); // show ₹0.00 immediately instead of the "₹—" placeholder

  // A Firestore listener that errors (rules, a missing index, a transient
  // disconnect) must never leave a field stuck on its placeholder. Surface the
  // real reason instead of failing silently.
  const onErr = (what: string) => (e: any) => {
    console.error(`[bundlestamp] ${what} listener failed:`, e);
    toast(`Couldn't load ${what}: ${e?.message || e}`, "err");
  };

  unsubs.push(onSnapshot(doc(db, "config", "app"), (s) => {
    priceCache = (s.data()?.price_per_page_paise as number) || 25;
    render(lastBalance);
  }, onErr("pricing")));
  unsubs.push(onSnapshot(doc(db, "wallets", user.uid), (s) => {
    const w = s.data();
    lastBalance = (w?.balance_paise as number) || 0;
    // Backend stamps these at wallet creation; used for the welcome nudge only.
    bonusInfo = w && w.signup_bonus_paise > 0
      ? { paise: w.signup_bonus_paise, pages: w.signup_bonus_pages || 0 }
      : undefined;
    render(lastBalance);
  }, onErr("wallet balance")));

  unsubs.push(onSnapshot(
    query(collection(db, "devices"), where("uid", "==", user.uid)),
    (snap) => renderDevices(snap.docs), onErr("devices")));

  unsubs.push(onSnapshot(
    query(collection(db, "jobs"), where("uid", "==", user.uid),
      orderBy("created_at", "desc"), limit(50)),
    (snap) => renderJobs(snap.docs), onErr("usage")));
}

function renderDevices(docs: any[]) {
  const ul = document.getElementById("devices");
  if (!ul) return;
  const active = docs.filter((d) => d.data().status !== "revoked");
  ob.devices = active.length;
  ul.innerHTML = active.length ? "" : `<li class="empty-rich">
      <span class="er-ic">${I.device}</span>
      <div><b>No devices yet</b><span>Download BundleStamp, then activate it with the code it shows.</span></div>
      <button class="btn small" data-open-dl>${I.download} Download</button>
    </li>`;
  ul.querySelector<HTMLButtonElement>("[data-open-dl]")?.addEventListener("click", () => openDownload("device_empty"));
  active.forEach((d) => {
    const data = d.data();
    const li = document.createElement("li");
    li.innerHTML = `<span class="dev-name"><span class="dev-ico">${I.device}</span>
        <span class="dev-meta"><b>${data.name || "PC"}</b><span>${data.platform || "Device"}</span></span></span>
      <button class="btn small danger" data-revoke="${d.id}">Revoke</button>`;
    ul.appendChild(li);
  });
  ul.querySelectorAll<HTMLButtonElement>("[data-revoke]").forEach((b) =>
    (b.onclick = async () => {
      try { await revokeDevice(b.dataset.revoke!); toast("Device revoked."); }
      catch (e: any) { toast(e.message, "err"); }
    }));
}

function renderJobs(docs: any[]) {
  const tb = document.getElementById("jobs");
  if (!tb) return;

  // Roll up summary stats + a sparkline from the user's recent jobs.
  let totalCost = 0, totalPages = 0, exports = 0;
  const costs: number[] = [];
  docs.forEach((d) => {
    const j = d.data();
    const pages = j.actual_pages ?? j.total_pages ?? 0;
    const cost = j.captured_paise ?? j.hold_amount_paise ?? 0;
    if (j.status === "committed") { totalCost += cost; totalPages += pages; exports++; costs.push(cost); }
  });
  ob.exports = exports;
  const set = (id: string, v: string) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  set("statSpent", fmt(totalCost));
  set("statPages", totalPages.toLocaleString());
  set("statExports", String(exports));

  const sparkEl = document.getElementById("spark");
  if (sparkEl && costs.length) {
    const recent = costs.slice(0, 10).reverse();
    const max = Math.max(...recent, 1);
    sparkEl.innerHTML = recent.map((c) => `<i style="height:${Math.max(8, Math.round((c / max) * 100))}%"></i>`).join("");
  }

  if (!docs.length) {
    tb.innerHTML = `<tr><td colspan="5"><div class="empty-rich center">
        <span class="er-ic">${I.layers}</span>
        <div><b>No exports yet</b><span>Your exported bundles and their page counts will appear here.</span></div>
      </div></td></tr>`;
    return;
  }
  tb.innerHTML = docs.map((d) => {
    const j = d.data();
    const date = j.created_at?.toDate ? j.created_at.toDate().toLocaleString() : "—";
    const pages = j.actual_pages ?? j.total_pages ?? 0;
    const cost = j.captured_paise ?? j.hold_amount_paise ?? 0;
    const cls = j.status === "committed" ? "ok" : j.status === "cancelled" ? "muted" : "live";
    return `<tr><td>${date}</td><td><span class="tagcell">${j.mode || "—"}</span></td>
      <td class="num">${pages.toLocaleString()}</td>
      <td class="num">${fmt(cost)}</td>
      <td><span class="badge ${cls} dot">${j.status}</span></td></tr>`;
  }).join("");
}

// ---- actions ----------------------------------------------------------------
async function topup(amountPaise: number) {
  try {
    const order = await createTopup(amountPaise);
    track("begin_checkout", { value: amountPaise / 100, currency: "INR" });
    const rzp = new Razorpay({
      key: order.key_id, amount: order.amount_paise, currency: order.currency,
      order_id: order.order_id, name: "BundleStamp", description: "Wallet top-up",
      prefill: { email: auth.currentUser?.email || "" },
      theme: { color: "#4F46E5" },
      handler: async (resp: any) => {
        // Credit synchronously so the balance moves immediately. The webhook is
        // the failsafe: if this verify call fails (network, etc.) the wallet
        // still gets credited once Razorpay delivers payment.captured.
        try {
          await verifyTopup({
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          track("purchase", { transaction_id: order.order_id, value: amountPaise / 100, currency: "INR" });
          toast("Payment received — balance updated.");
        } catch (e: any) {
          console.error("[bundlestamp] topup verify failed:", e);
          toast("Payment received — balance updates shortly.");
        }
      },
    });
    rzp.on("payment.failed", (r: any) => toast(r.error?.description || "Payment failed", "err"));
    rzp.open();
  } catch (e: any) {
    toast(e.message, "err");
  }
}

async function doApprove() {
  const input = document.getElementById("userCode") as HTMLInputElement;
  const code = input.value.trim().toUpperCase();
  if (!code) return;
  const btn = document.getElementById("approve") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Activating…"; }
  try {
    const r = await approveDevice(code);
    track("device_activated");
    toast(`Activated “${r.device_name}”. Return to the app.`);
    showActivated(r.device_name);
  } catch (e: any) {
    toast(e.message || "Activation failed", "err");
    if (btn) { btn.disabled = false; btn.innerHTML = `${I.check} Activate`; }
  }
}

/** Replace the activation card with a clear "you're done" confirmation. */
function showActivated(name: string) {
  const card = document.querySelector(".card.activate");
  if (card) {
    card.innerHTML =
      `<div class="activated">
        <div class="seal-badge">${I.check}</div>
        <h2>Device activated</h2>
        <p class="muted"><b>${name || "Your PC"}</b> is now connected to your account.
        You can close this tab and return to the BundleStamp app.</p>
      </div>`;
  }
}

// ---- boot -------------------------------------------------------------------
let loggedThisSession = false;
(async () => {
  void initAnalytics();
  void refreshPublicPricing(); // fills [data-pub] once the live numbers arrive
  await handleEmailLink();
  onAuthStateChanged(auth, async (user) => {
    removeOverlays(); // clear any open sign-in / download modal on auth change
    if (user) {
      identify(user.uid);
      if (!loggedThisSession) {
        loggedThisSession = true;
        track("login", { method: user.providerData[0]?.providerId || "email_link" });
      }
      const claims = (await user.getIdTokenResult()).claims;
      // Fire-and-forget: creates the wallet (and seeds the welcome credit) for
      // brand-new web signups. The dashboard's Firestore listener shows the
      // balance the moment the server writes it; failures are harmless — the
      // wallet is also ensured on device activation, top-up and export.
      void ensureWalletWeb().catch(() => {});
      renderDashboard(user, claims.admin === true);
      maybeAutoTour();
    } else {
      unsubs.forEach((u) => u());
      unsubs = [];
      renderPublic();
    }
  });
})();
